/* eslint-disable @typescript-eslint/no-magic-numbers */
import { inject, Injectable, signal } from '@angular/core';
import { Subject, concatMap, of, takeUntil, tap, catchError, delay } from 'rxjs';
import { ToothSide } from '@libs/ui-lib';
import { PerioatorCommandMessageResponse, PerioatorDataService } from '@ffd/api-client';
import {
  DataModel,
  GoToSideInstr,
  GoToToothInstr,
  MeasurementInstr,
  MeasurementAddendumInstr,
  MissingToothInstr,
  SkipToothInstr,
  Instruction,
  ClearInstr,
  Surface,
  MeasurementField,
  DataInputModeInstr,
} from './models';

export interface VoicePerioCallbacks {
  applyMeasurement: (pocketDepth?: number, bop?: boolean, sop?: boolean, mobility?: number, furcation?: number, recession?: number) => void;
  applyMeasurementAddendum: (
    toothPosition: number | null,
    side: 'buccal' | 'lingual' | null,
    surfaces: Surface[] | null,
    depth: number | null,
    bop: boolean | null,
    suppuration: boolean | null,
    mobility: number | null,
    furcation: number | null,
    recession: number | null,
  ) => void;
  moveToPosition: (position: number | null, relative: boolean, surface: Surface | null) => void;
  moveToSide: (side: ToothSide) => void;
  markToothAsMissing: (toothPosition: number) => void;
  clearMeasurements: (
    toothPosition: number | null,
    side: 'buccal' | 'lingual' | 'both' | null,
    surfaces: Surface[] | null,
    measurements: MeasurementField[] | null,
  ) => void;
  setInputMode: (mode: 'pocketDepth' | 'recession') => void;
  getInputMode: () => 'pocketDepth' | 'recession';
}

@Injectable({ providedIn: 'root' })
export class VoicePerioService {
  private readonly perioatorDataService = inject(PerioatorDataService);
  private destroy$ = new Subject<void>();

  public isAiResponseLoading = signal(false);
  public aiCommandQueueLength = signal(0);
  public logs = signal<string[]>([]);

  private previousInput: string | null = null;
  private readonly previousInputWords: string[] = [];
  private readonly MAX_PREVIOUS_WORDS = 20;
  private currentInputMode: 'pocketDepth' | 'recession' = 'pocketDepth';
  // Whenever a new voice command is sent, it is queued here
  private voiceCommandQueue$ = new Subject<string>();
  private isProcessingVoiceCommand = false;
  private isQueueSetup = false;
  private aiCommandStartTime: number | null = null;

  // There are multiple instructions which can come out from a voice command, those are queued here
  private instructionQueue$ = new Subject<Instruction>();
  private isProcessingInstructions = false;
  private isInstructionQueueSetup = false;

  private callbacks: VoicePerioCallbacks | null = null;

  /**
   * Initialize the voice perio service with callbacks
   */
  public init(callbacks: VoicePerioCallbacks): void {
    this.callbacks = callbacks;
    this.setupAiCommandQueue();
    this.setupInstructionQueue();
  }

  /**
   * Reset voice perio state when stopping voice mode
   * Clears all pending operations and state
   */
  public reset(): void {
    this.destroy$.next();

    this.voiceCommandQueue$.complete();
    this.instructionQueue$.complete();
    this.voiceCommandQueue$ = new Subject<string>();
    this.instructionQueue$ = new Subject<Instruction>();

    // Recreate destroy$ for new subscriptions
    this.destroy$ = new Subject<void>();

    // Reset all state signals and flags
    this.isAiResponseLoading.set(false);
    this.aiCommandQueueLength.set(0);
    this.logs.set([]);
    this.previousInput = null;
    this.previousInputWords.length = 0;
    this.isProcessingVoiceCommand = false;
    this.isProcessingInstructions = false;
    this.aiCommandStartTime = null;

    this.isQueueSetup = false;
    this.isInstructionQueueSetup = false;
  }

  /**
   * Clean up subscriptions when component is destroyed
   */
  public destroy(): void {
    this.reset();
    this.callbacks = null;
  }

  public sendVoiceCommand(command: string): void {
    // Always queue the command to maintain order
    this.aiCommandQueueLength.update((count) => count + 1);
    this.voiceCommandQueue$.next(command);
  }

  public setCurrentInputMode(mode: 'pocketDepth' | 'recession'): void {
    this.currentInputMode = mode;
  }

  public isAiCommandProcessing(): boolean {
    return this.isProcessingVoiceCommand;
  }

  public isInstructionProcessing(): boolean {
    return this.isProcessingInstructions;
  }

  /**
   * Queue any instruction type for sequential processing
   * Useful for testing or manual instruction queuing
   */
  public queueInstruction(instruction: Instruction): void {
    this.instructionQueue$.next(instruction);
  }

  /**
   * Processes simple measurement commands locally without AI
   * Returns null if the command cannot be processed locally
   */
  private processCommandLocally(text: string): DataModel | null {
    const words = text.toLowerCase().replaceAll(',', '').replaceAll('.', '').split(' ');
    if (words.length === 0) {
      return { instructions: [], request_more_input: false };
    }

    // Check if all words can be processed locally
    if (!this.canProcessAllWordsLocally(words)) {
      return null;
    }

    return this.parseWordsToInstructions(words);
  }

  private canProcessAllWordsLocally(words: string[]): boolean {
    const MAX_POCKET_DEPTH = 9;
    const numberWords: Record<string, number> = {
      one: 1,
      two: 2,
      three: 3,
      four: 4,
      five: 5,
      six: 6,
      seven: 7,
      eight: 8,
      nine: 9,
    };

    return words.every((word) => {
      const isDigit =
        (/^\d+$/.test(word) && Number.parseInt(word, 10) >= 1 && Number.parseInt(word, 10) <= MAX_POCKET_DEPTH) ||
        (numberWords[word] !== undefined && numberWords[word] >= 1 && numberWords[word] <= MAX_POCKET_DEPTH);
      const isBleedingKeyword = ['b', 'bleed', 'bleeding'].includes(word);
      const isSuppurationKeyword = ['s', 'suppuration', 'pus'].includes(word);
      return isDigit || isBleedingKeyword || isSuppurationKeyword;
    });
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  private parseWordsToInstructions(words: string[]): DataModel {
    const instructions: MeasurementInstr[] = [];
    let currentMeasurement: MeasurementInstr | null = null;
    // replace number words with digits
    const numberWords: Record<string, number> = {
      one: 1,
      two: 2,
      three: 3,
      four: 4,
      five: 5,
      six: 6,
      seven: 7,
      eight: 8,
      nine: 9,
    };
    words = words.map((word) => (numberWords[word] === undefined ? word : numberWords[word].toString()));

    const createNewMeasurement = (): MeasurementInstr => ({
      type: 'measurement',
      depth: null,
      bop: null,
      suppuration: null,
      recession: null,
      mobility: null,
      furcation: null,
    });

    for (const word of words) {
      if (/^\d+$/.test(word)) {
        // If we have a current measurement, push it before starting a new one
        if (currentMeasurement !== null) {
          instructions.push(currentMeasurement);
        }
        currentMeasurement = createNewMeasurement();
        const value = Number.parseInt(word, 10);
        // Use recession or depth based on current input mode - read from callback to ensure sync
        const currentMode = this.callbacks?.getInputMode() ?? this.currentInputMode;
        if (currentMode === 'recession') {
          currentMeasurement.recession = value;
        } else {
          currentMeasurement.depth = value;
        }
      } else if (['b', 'bleed', 'bleeding'].includes(word)) {
        if (currentMeasurement === null) {
          currentMeasurement = createNewMeasurement();
        }
        currentMeasurement.bop = true;
      } else if (['s', 'suppuration', 'pus', 'separation'].includes(word)) {
        if (currentMeasurement === null) {
          currentMeasurement = createNewMeasurement();
        }
        currentMeasurement.suppuration = true;
      }
    }

    // Don't forget to add the last measurement
    if (currentMeasurement !== null) {
      instructions.push(currentMeasurement);
    }

    return { instructions, request_more_input: false };
  }

  private setupAiCommandQueue(): void {
    if (this.isQueueSetup) {
      return;
    }

    this.isQueueSetup = true;

    this.voiceCommandQueue$
      .pipe(
        concatMap((command) => {
          this.logs.update((logs) => [...logs, `\nProcessing user command: ${command}`]);
          this.isProcessingVoiceCommand = true;
          this.isAiResponseLoading.set(true);
          this.aiCommandStartTime = Date.now();

          // Try to process locally first, but only if we're not waiting for continuation
          // If previousInput exists, we need to concatenate and send to AI regardless
          const localResult = this.previousInput ? null : this.processCommandLocally(command);
          if (localResult !== null) {
            // Process locally - create mock OpenAI response structure
            const mockResponse: PerioatorCommandMessageResponse = {
              message: JSON.stringify(localResult),
            } as PerioatorCommandMessageResponse;

            // Handle the local response and complete processing
            return of(mockResponse).pipe(
              tap((response) => {
                this.handlePerioCommand(command, command, response);
              }),
              tap(() => {
                this.isProcessingVoiceCommand = false;
                this.isAiResponseLoading.set(false);
                this.aiCommandQueueLength.update((count) => Math.max(0, count - 1));
              }),
            );
          }
          // Fall back to AI processing
          const input = this.previousInput ? `${this.previousInput} ${command}` : command;
          // Read current mode from callback to ensure sync
          const currentMode = this.callbacks?.getInputMode() ?? this.currentInputMode;
          const inputModeName = currentMode === 'pocketDepth' ? 'pocket depth' : 'recession';
          // Construct the enhanced input with previous context
          const enhancedInput =
            this.previousInputWords.length > 0
              ? `Previously processed dictation: ${this.previousInputWords.join(
                  ' ',
                )}\nUser input: ${input}\nCurrent data input mode: ${inputModeName} mode`
              : `User input: ${input}\nCurrent data input mode: ${inputModeName} mode`;
          return this.perioatorDataService
            .getPerioatorCommand({
              userMessage: enhancedInput,
            })
            .pipe(
              tap((response) => {
                this.handlePerioCommand(command, input, response);
              }),
              catchError((error) => {
                // eslint-disable-next-line no-console
                console.error('Error getting AI response:', error);
                return of(null);
              }),
              tap(() => {
                this.isProcessingVoiceCommand = false;
                this.isAiResponseLoading.set(false);
                this.aiCommandQueueLength.update((count) => Math.max(0, count - 1));
              }),
              // Add takeUntil to ensure this observable completes even if destroy$ fires
              // This prevents the concatMap from hanging if the service is destroyed during a request
              takeUntil(this.destroy$),
            );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  private setupInstructionQueue(): void {
    if (this.isInstructionQueueSetup) {
      return;
    }

    this.isInstructionQueueSetup = true;

    this.instructionQueue$
      .pipe(
        concatMap((instruction) => {
          this.isProcessingInstructions = true;

          if (!this.callbacks) {
            // eslint-disable-next-line no-console
            console.warn('VoicePerioService callbacks not initialized');
            return of(null);
          }

          // Process different instruction types sequentially
          switch (instruction.type) {
            case 'measurement': {
              const measurement = instruction as MeasurementInstr;
              this.callbacks.applyMeasurement(
                measurement.depth ?? undefined,
                !!measurement.bop,
                !!measurement.suppuration,
                measurement.mobility ?? undefined,
                measurement.furcation ?? undefined,
                measurement.recession ?? undefined,
              );
              break;
            }
            case 'goto-tooth': {
              const goToToothInstruction = instruction as GoToToothInstr;
              this.callbacks.moveToPosition(goToToothInstruction.tooth, goToToothInstruction.relative, goToToothInstruction.surface);
              break;
            }
            case 'goto-side': {
              const goToSideInstruction = instruction as GoToSideInstr;
              this.callbacks.moveToSide(goToSideInstruction.side);
              break;
            }
            case 'skip-tooth': {
              const skipToothInstruction = instruction as SkipToothInstr;
              this.callbacks.moveToPosition(skipToothInstruction.tooth_index + 1, false, null);
              break;
            }
            case 'missing-tooth': {
              const missingToothInstruction = instruction as MissingToothInstr;
              this.callbacks.markToothAsMissing(missingToothInstruction.tooth);
              break;
            }
            case 'clear': {
              const clearInstruction = instruction as ClearInstr;
              this.callbacks.clearMeasurements(
                clearInstruction.tooth,
                clearInstruction.side,
                clearInstruction.surfaces,
                clearInstruction.measurements,
              );
              break;
            }
            case 'measurement-addendum': {
              const addendumInstruction = instruction as MeasurementAddendumInstr;
              this.callbacks.applyMeasurementAddendum(
                addendumInstruction.tooth,
                addendumInstruction.side,
                addendumInstruction.surfaces,
                addendumInstruction.depth,
                addendumInstruction.bop ?? null,
                addendumInstruction.suppuration ?? null,
                addendumInstruction.mobility,
                addendumInstruction.furcation,
                addendumInstruction.recession,
              );
              break;
            }
            case 'data-input-mode': {
              const dataInputModeInstruction = instruction as DataInputModeInstr;
              const mode = dataInputModeInstruction.mode === 'pocket_depth' ? 'pocketDepth' : 'recession';
              this.currentInputMode = mode;
              this.callbacks.setInputMode(mode);
              break;
            }
            default: {
              // eslint-disable-next-line no-console
              console.warn('Unknown step type:', instruction);
            }
          }

          // Return an observable that completes after the instruction is processed
          // NOTE: Callbacks use queueMicrotask + setTimeout internally, so we need enough delay
          // for those async operations to complete before processing the next instruction
          // Mode switches need extra time for focus to settle properly
          const INSTRUCTION_DELAY_MS = instruction.type === 'data-input-mode' ? 350 : 250;
          return of(null).pipe(
            delay(INSTRUCTION_DELAY_MS), // Wait between instructions to ensure proper processing
            tap(() => {
              this.isProcessingInstructions = false;
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private handlePerioCommand(currentCommand: string, fullInput: string, _response: PerioatorCommandMessageResponse): void {
    // Calculate response time
    const responseTime = this.aiCommandStartTime ? Date.now() - this.aiCommandStartTime : null;

    if (_response.message) {
      const answer: DataModel = JSON.parse(_response.message);

      this.logs.update((logs) => [
        ...logs,
        `\nResponse: ${JSON.stringify({
          currentCommand,
          fullInput,
          previousInput: this.previousInput,
          instructions: answer.instructions,
          responseTimeMs: responseTime,
          requestMoreInput: answer.request_more_input,
        })}`,
      ]);

      if (answer.request_more_input) {
        // Concatenate only the new command with the previous input
        const newPreviousInput = this.previousInput ? `${this.previousInput} ${currentCommand}` : currentCommand;
        this.previousInput = newPreviousInput;
      } else {
        this.previousInput = null;
        // Store words from the processed input (up to MAX_PREVIOUS_WORDS)
        const words = fullInput.split(' ');
        this.previousInputWords.push(...words);
        // Keep only the last MAX_PREVIOUS_WORDS
        if (this.previousInputWords.length > this.MAX_PREVIOUS_WORDS) {
          this.previousInputWords.splice(0, this.previousInputWords.length - this.MAX_PREVIOUS_WORDS);
        }
        // Queue all instructions for sequential processing with animation if AI does not need more input
        answer.instructions.forEach((instruction) => {
          this.queueInstruction(instruction);
        });
      }
    }
  }
}
